import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const EquipmentManufacturerContext = createContext();
 
export const useEquipmentManufacturers = () => {
  const context = useContext(EquipmentManufacturerContext);
  if (!context) {
    throw new Error('useEquipmentManufacturers must be used within a EquipmentManufacturerProvider');
  }
  return context;
};

export const EquipmentManufacturerProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [equipmentManufacturers, setEquipmentManufacturers] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/equipment-manufacturers');

  const getEquipmentManufacturers = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('equipment-manufacturers?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('equipment-manufacturers?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setEquipmentManufacturers(response.data.equipmentManufacturers || []);
       
        
        if(filters){
          return response.data.equipmentManufacturers;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch equipment manufacturers information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getEquipmentManufacturerByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`equipment-manufacturers/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let equipmentManufacturers = {'data':[response.data.equipmentManufacturer]}
      setEquipmentManufacturers(equipmentManufacturers || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/equipment-manufacturers/edit/'+id))){
        if (getToken()) {
        
          
          await getEquipmentManufacturerByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getEquipmentManufacturerByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getEquipmentManufacturers(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getEquipmentManufacturers, searchParams, initialized,hideLoading,setApi]);

    
  const addEquipmentManufacturer = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append image if provided
      if (data.image) {
        formData.append('image', data.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('equipment-manufacturers', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the equipment manufacturers list
        await getEquipmentManufacturers();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Equipment Manufacturer'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create equipment manufacturer'
      });
      return { status: 500, error: error.message };
    }
  };
  
  const getEquipmentManufacturerById = useCallback(async (id) => {
   // let manufacturer = equipmentManufacturers?.data?.find(equipmentManufacturer => Number(equipmentManufacturer.id) === Number(id));
 
    //return {'status':200,'data':manufacturer};
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`equipment-manufacturers/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.equipmentManufacturer };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch equipment manufacturer details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch equipment manufacturer details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateEquipmentManufacturer = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append image if provided
      if (updatedData.image) {
        formData.append('image', updatedData.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(updatedData));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`equipment-manufacturers/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the equipment manufacturers list
        await getEquipmentManufacturers();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Equipment Manufacturer'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update equipment manufacturer'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteEquipmentManufacturer = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`equipment-manufacturers/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the equipment manufacturers list
        await getEquipmentManufacturers();
        navigate(getRoutePath('/equipment-manufacturers'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete equipment manufacturer'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete equipment manufacturer'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <EquipmentManufacturerContext.Provider
      value={{
        equipmentManufacturers,
        addEquipmentManufacturer,
        getEquipmentManufacturerById,
        updateEquipmentManufacturer,
        deleteEquipmentManufacturer,
        getEquipmentManufacturers,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </EquipmentManufacturerContext.Provider>
  );
};

export default EquipmentManufacturerContext; 